import { useState } from 'react';
import type { z } from 'zod';
import { Link } from 'react-router-dom';
import { forgotPassword, ForgotPasswordBodySchema } from '../lib/api/auth.gen';
import { Form, FormError, FormField, FormSubmit, Input, Stack, useZodForm } from '../components/ui';

export function ForgotPasswordPage(): JSX.Element {
  const [status, setStatus] = useState<'idle' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const form = useZodForm(ForgotPasswordBodySchema, { defaultValues: { email: '' } });

  const handleSubmit = async (values: z.input<typeof ForgotPasswordBodySchema>) => {
    setError(null);
    try {
      await forgotPassword(values);
      setStatus('sent');
    } catch (err) {
      setError('We could not send the reset link. Please try again.');
    }
  };

  return (
    // Same deliberate `max-w-md` exception LoginPage.tsx's own header comment
    // documents — this page also renders inside PublicLayout's chrome.
    <Stack gap={8} className="mx-auto max-w-md">
      <Stack gap={3} className="text-center">
        <h1 className="text-3xl font-semibold text-foreground">Reset your password</h1>
        <p className="text-sm text-muted-foreground">
          Enter your account email and we will send you a reset link.
        </p>
      </Stack>

      <Form form={form} onSubmit={handleSubmit} className="rounded-hairline border border-border bg-card space-y-4 p-6">
        <FormField name="email" label="Email" required>
          {(field) => <Input type="email" className="w-full" placeholder="you@agency.com" {...field} />}
        </FormField>

        <FormError message={error} />
        {status === 'sent' && (
          <p className="rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary">
            If this email exists, a reset link is on the way.
          </p>
        )}

        <FormSubmit className="w-full" pendingLabel="Sending…">
          Send reset link
        </FormSubmit>
      </Form>

      <p className="text-center text-sm text-muted-foreground">
        Remembered your password?{' '}
        <Link to="/login" className="text-primary hover:underline">
          Log in
        </Link>
        .
      </p>
    </Stack>
  );
}
